// The operator page (slice 90, ADR 0090): an animated map of what runs, what each
// part is carrying, and why something stopped. Only the platform operator can open
// it (the server refuses anyone else); every status and sentence comes from the
// server and the page works nothing out.
//
// Click a box to open it. The open box is kept in the address (?node=) so a reload
// or a shared link lands on the same view.

import { useEffect, useState, type JSX } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../api/client.js';
import type { OpsOverview, OpsStatus } from '../api/ops-types.js';
import { useAuth } from '../auth/AuthContext.js';
import { OpsDetail } from './ops/OpsDetail.js';
import { OpsMap } from './ops/OpsMap.js';
import { NOTIFY_LABEL, STATUS_LABEL, ago, n0, pct, perMinute, span } from './ops/format.js';
import type { NodeId } from './ops/map-model.js';

const REFRESH_MS = 5000;
const OPENABLE: readonly NodeId[] = [
  'api',
  'worker',
  'relay',
  'jobs',
  'database',
  'providers',
  'edge',
];

function asNode(v: string | null): NodeId | null {
  return OPENABLE.includes(v as NodeId) ? (v as NodeId) : null;
}

function tile(
  k: string,
  v: string,
  u: string | undefined,
  st: OpsStatus,
): { k: string; v: string; u?: string; st: OpsStatus } {
  return u === undefined ? { k, v, st } : { k, v, u, st };
}

export function OpsPage(): JSX.Element {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const selected = asNode(params.get('node'));
  const [paused, setPaused] = useState(false);
  const [tick, setTick] = useState(0);

  const q = useQuery({
    queryKey: ['ops', 'overview'],
    queryFn: () => apiFetch<OpsOverview>('/ops/overview'),
    refetchInterval: paused ? false : REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  // "refreshed n s ago" counts on the page's own clock between refreshes.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  void tick;

  const select = (id: NodeId): void => {
    const next = new URLSearchParams(params);
    if (selected === id) next.delete('node');
    else next.set('node', id);
    setParams(next, { replace: true });
  };

  const err = q.error;
  const forbidden = err instanceof ApiError && err.status === 403;
  const ov = q.data;

  return (
    <main className="page ops-page">
      <header className="page-header">
        <div>
          <h1>Operator</h1>
          <p className="muted">
            Signed in as <strong>{user?.email}</strong> ·{' '}
            <Link to="/home">Back to your outlets</Link>
          </p>
        </div>
        <div className="header-actions ops-meta">
          {ov ? (
            <span className="muted" data-testid="ops-refreshed">
              Updated {Math.max(0, Math.round((Date.now() - q.dataUpdatedAt) / 1000))} s ago
            </span>
          ) : null}
          <button type="button" onClick={() => setPaused((p) => !p)} aria-pressed={paused}>
            {paused ? 'Resume' : 'Pause'}
          </button>
        </div>
      </header>

      {q.isLoading ? <p>Loading…</p> : null}
      {forbidden ? (
        <p className="error" role="alert">
          This page is for the platform operator. Your account does not have it.
        </p>
      ) : err ? (
        <p className="error" role="alert">
          Could not load the operator view. {ov ? 'Showing the last good numbers.' : ''}
        </p>
      ) : null}

      {ov ? (
        <>
          <p className="note">
            The API and the worker each write a heartbeat and a summary of themselves to the
            database every ten seconds. This page reads that summary, so it can lag by that long.
            Nothing here is a request or a person: only counts, times and short scrubbed error
            sentences.
          </p>

          <section className="ops-tiles" aria-label="Summary">
            {[
              tile(
                'Overall',
                STATUS_LABEL[ov.overall],
                undefined,
                ov.overall === 'unknown' ? 'ok' : ov.overall,
              ),
              tile(
                'Need attention',
                String(ov.parts.filter((p) => p.status === 'warn' || p.status === 'bad').length),
                `of ${ov.parts.length}`,
                ov.parts.some((p) => p.status === 'bad')
                  ? 'bad'
                  : ov.parts.some((p) => p.status === 'warn')
                    ? 'warn'
                    : 'ok',
              ),
              tile(
                'Machines running',
                String(
                  [...ov.services.api.machines, ...ov.services.worker.machines].filter(
                    (m) => m.condition === 'up',
                  ).length,
                ),
                `of ${ov.services.api.machines.length + ov.services.worker.machines.length}`,
                [...ov.services.api.machines, ...ov.services.worker.machines].some(
                  (m) => m.condition !== 'up',
                )
                  ? 'warn'
                  : 'ok',
              ),
              tile('API calls', perMinute(ov.services.api.traffic.callsPerMinute), '/min', 'ok'),
              tile(
                'API server errors',
                pct(ov.services.api.traffic.errorRate),
                'of calls',
                ov.services.api.status === 'bad' && ov.services.api.traffic.errorRate > 0
                  ? 'bad'
                  : ov.services.api.traffic.errorRate >= 0.015
                    ? 'warn'
                    : 'ok',
              ),
              tile(
                'Outbox waiting',
                ov.relay.sampled ? n0(ov.relay.depth) : '—',
                undefined,
                ov.relay.status,
              ),
            ].map((c) => (
              <div key={c.k} className={`ops-tile${c.st !== 'ok' ? ` st-${c.st}` : ''}`}>
                <div className="k">{c.k}</div>
                <div className={`v${c.v.length > 7 ? ' long' : ''}`}>
                  {c.v}
                  {c.u ? <small>{c.u}</small> : null}
                </div>
              </div>
            ))}
          </section>

          <section className="card ops-map-card" aria-label="Service map">
            <div className="ops-map-head">
              <h2>What is running</h2>
              <span className="muted">
                Dots are traffic: more dots, more calls. Dashed boxes are not measured.
              </span>
            </div>
            <OpsMap overview={ov} selected={selected} onSelect={select} paused={paused} />
            <div className="ops-legend">
              <span>
                <i className="ops-dot ok" /> Healthy traffic
              </span>
              <span>
                <i className="ops-dot warn" /> Slow or degraded
              </span>
              <span>
                <i className="ops-dot bad" /> Failing: dots fade before arriving
              </span>
              <span>
                <i className="ops-dot idle" /> No traffic
              </span>
            </div>
            <ul className="ops-unmeasured">
              {ov.notMeasured.map((n) => (
                <li key={n.id}>
                  <b>{n.label}.</b> {n.note}
                </li>
              ))}
            </ul>
          </section>

          <div className={`ops-detail-wrap${selected ? ' open' : ''}`}>
            <div className="ops-detail-clip">
              <section className="card ops-detail" aria-live="polite">
                {selected ? <OpsDetail node={selected} ov={ov} /> : null}
              </section>
            </div>
          </div>

          <section className="card ops-alerts-card" aria-label="Alerts">
            <h2>Alerts</h2>
            {ov.alerts.length === 0 ? (
              <p className="muted">No alert in the last day.</p>
            ) : (
              <ul className="ops-events ops-alerts">
                {ov.alerts.map((a) => {
                  const open = a.resolvedAtMs === null;
                  return (
                    <li key={a.id} className={open ? 'open' : 'ended'}>
                      <time>{ago(a.openedAtMs, ov.generatedAtMs)}</time>
                      <span className={`sev ${open ? a.severity : 'ok'}`} aria-hidden="true" />
                      <span>
                        <b>
                          {open ? 'Open.' : `Ended after ${span(a.resolvedAtMs - a.openedAtMs)}.`}
                        </b>{' '}
                        {a.summary}
                        {a.notify ? <span className="prev"> {NOTIFY_LABEL[a.notify]}.</span> : null}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="note">
              An alert opens when a part is failing, and ends once it has been healthy for a couple
              of minutes. A part nothing has reported on neither opens one nor ends one.
            </p>
          </section>

          <section className="card ops-events-card" aria-label="Recent events">
            <h2>Recent events</h2>
            {ov.events.length === 0 ? (
              <p className="muted">No crash or failed job in the last day.</p>
            ) : (
              <ul className="ops-events">
                {ov.events.map((e) => (
                  <li key={`${e.part}-${e.atMs}-${e.text}`}>
                    <time>{ago(e.atMs, ov.generatedAtMs)}</time>
                    <span className={`sev ${e.severity}`} aria-hidden="true" />
                    <span>{e.text}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}
    </main>
  );
}
